import { gql } from '@apollo/client';
import type { AdminQuestAudience, AdminQuestExclusions } from '../quests/quest-shared';

/**
 * Shared GraphQL documents + types for the admin Bounties pages (#4118) — the quest
 * admin's keystone pattern: one fragment, every document interpolates it.
 */

export type BountyStatus = 'draft' | 'live' | 'archived';

/** A bounty, or an onboarding-series item that is not a task (#4215) — shared/bounties/kind.ts. */
export type BountyKind = 'task' | 'message' | 'suggestions';

export interface AdminBounty {
  id: string;
  slug: string;
  name: string;
  emoji: string | null;
  color: string | null;
  kind: BountyKind;
  stepCode: string;
  target: number | null;
  stepOverrides: Record<string, string | number> | null;
  pointsReward: number;
  /** `each` (every time, no period) is for engine-run rows only. */
  cadence: 'once' | 'daily' | 'weekly' | 'monthly' | 'each';
  seriesKey: string | null;
  seriesDay: number | null;
  enrollment: 'everyone' | 'optin' | 'invite';
  channelNotify: boolean;
  channelPin: boolean;
  channelEmail: boolean;
  isInDailyEmail: boolean;
  showOnPoints: boolean;
  showOnBounties: boolean;
  notifyReminder: string | null;
  emailReminder: string | null;
  status: BountyStatus;
  audience: AdminQuestAudience | null;
  exclusions: AdminQuestExclusions | null;
  priority: number;
  eligibleSignupFrom: string | null;
  scheduledFor: string | null;
  scheduledReachBackDays: number | null;
  scheduleError: string | null;
  systemKey: string | null;
  inviteToken: string | null;
  config: Record<string, number> | null;
  publishedAt: string | null;
  createdAt: string;
  preview: {
    label: string;
    why: string;
    evidence: string;
    cta: string | null;
    opens: string;
    count: number | null;
    of: number | null;
  };
  audienceCount: number;
  audienceTotal: number;
  paidCount: number;
  deliveredCount: number;
  emailedCount: number;
  clickedCount: number;
}

export interface BountyCatalogEntry {
  code: string;
  adminLabel: string;
  adminMeasurement: string;
  completion: string;
  scope: string;
  bar: number | null;
  defaultLabel: string;
  defaultWhy: string;
  defaultEvidence: string;
  defaultHref: string;
  defaultOpens: string;
  defaultMinutes: number | null;
  defaultCta: string | null;
  isSeriesItemOnly: boolean;
  productionReady: boolean;
  onceVerdict: string | null;
  seriesVerdict: string | null;
  recurringVerdict: string | null;
  on: boolean;
  bounties: Array<{
    id: string;
    name: string;
    status: BountyStatus;
    pointsReward: number;
    seriesKey: string | null;
    cadence: AdminBounty['cadence'];
    enrollment: AdminBounty['enrollment'];
  }>;
}

export const BOUNTY_FIELDS = gql`
  fragment AdminBountyFields on AdminBounty {
    id
    slug
    name
    emoji
    color
    kind
    stepCode
    target
    stepOverrides
    pointsReward
    cadence
    seriesKey
    seriesDay
    enrollment
    channelNotify
    channelPin
    channelEmail
    isInDailyEmail
    showOnPoints
    showOnBounties
    notifyReminder
    emailReminder
    status
    audience {
      intents
      careerStages
      countryCodes
    }
    exclusions {
      signedUpBefore
      maxVotesCast
      maxCommentsWritten
      maxPostsRead
      minAccountAgeDays
      requireTier2Activated
      minReputationPoints
      profileCompleteness
    }
    priority
    eligibleSignupFrom
    scheduledFor
    scheduledReachBackDays
    scheduleError
    systemKey
    inviteToken
    config
    publishedAt
    createdAt
    preview {
      label
      why
      evidence
      cta
      opens
      count
      of
    }
    audienceCount
    audienceTotal
    paidCount
    deliveredCount
    emailedCount
    clickedCount
  }
`;

export const ADMIN_BOUNTIES = gql`
  ${BOUNTY_FIELDS}
  query AdminBounties {
    adminBounties {
      ...AdminBountyFields
    }
  }
`;

export const ADMIN_BOUNTY = gql`
  ${BOUNTY_FIELDS}
  query AdminBounty($id: ID!) {
    adminBounty(id: $id) {
      ...AdminBountyFields
    }
  }
`;

export const ADMIN_BOUNTY_CATALOG = gql`
  query AdminBountyCatalog {
    adminBountyCatalog {
      code
      adminLabel
      adminMeasurement
      completion
      scope
      bar
      defaultLabel
      defaultWhy
      defaultEvidence
      defaultHref
      defaultOpens
      defaultMinutes
      defaultCta
      isSeriesItemOnly
      productionReady
      onceVerdict
      seriesVerdict
      recurringVerdict
      on
      bounties {
        id
        name
        status
        pointsReward
        seriesKey
        cadence
        enrollment
      }
    }
  }
`;

export const ADMIN_BOUNTY_LOOKUP = gql`
  query AdminBountyLookup($userId: ID!) {
    adminBountyLookup(userId: $userId) {
      reason
      periodKey
      deliveredAt
      notifiedAt
      emailedAt
      completedAt
      paidAt
      pointsPaid
      bounty {
        id
        name
        slug
        cadence
        pointsReward
      }
    }
  }
`;

export const ADMIN_CREATE_BOUNTY = gql`
  ${BOUNTY_FIELDS}
  mutation AdminCreateBounty($input: AdminBountyInput!) {
    adminCreateBounty(input: $input) {
      ...AdminBountyFields
    }
  }
`;

export const ADMIN_UPDATE_BOUNTY = gql`
  ${BOUNTY_FIELDS}
  mutation AdminUpdateBounty($id: ID!, $input: AdminBountyInput!) {
    adminUpdateBounty(id: $id, input: $input) {
      ...AdminBountyFields
    }
  }
`;

export const ADMIN_SCHEDULE_BOUNTY = gql`
  ${BOUNTY_FIELDS}
  mutation AdminScheduleBounty($id: ID!, $goLiveAt: DateTime!, $reachBackDays: Int, $confirmEmailBlast: Boolean) {
    adminScheduleBounty(
      id: $id
      goLiveAt: $goLiveAt
      reachBackDays: $reachBackDays
      confirmEmailBlast: $confirmEmailBlast
    ) {
      ...AdminBountyFields
    }
  }
`;

export const ADMIN_UNSCHEDULE_BOUNTY = gql`
  ${BOUNTY_FIELDS}
  mutation AdminUnscheduleBounty($id: ID!) {
    adminUnscheduleBounty(id: $id) {
      ...AdminBountyFields
    }
  }
`;

/** "Fri, Oct 10, 9:00 AM" in the admin's own time zone. */
export function scheduleLabel(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export const ADMIN_SET_BOUNTY_STATUS = gql`
  ${BOUNTY_FIELDS}
  mutation AdminSetBountyStatus($id: ID!, $status: String!, $confirmEmailBlast: Boolean, $reachBackDays: Int) {
    adminSetBountyStatus(
      id: $id
      status: $status
      confirmEmailBlast: $confirmEmailBlast
      reachBackDays: $reachBackDays
    ) {
      ...AdminBountyFields
    }
  }
`;

export const ADMIN_DELETE_BOUNTY = gql`
  mutation AdminDeleteBounty($id: ID!) {
    adminDeleteBounty(id: $id)
  }
`;

export const ADMIN_TOGGLE_STEP_BOUNTY = gql`
  mutation AdminToggleStepBounty($stepCode: String!, $on: Boolean!) {
    adminToggleStepBounty(stepCode: $stepCode, on: $on) {
      id
    }
  }
`;

export const ADMIN_INVITE_TO_BOUNTY = gql`
  mutation AdminInviteToBounty($id: ID!, $userIds: [ID!]!) {
    adminInviteToBounty(id: $id, userIds: $userIds)
  }
`;

export const ADMIN_ROTATE_BOUNTY_INVITE = gql`
  mutation AdminRotateBountyInvite($id: ID!) {
    adminRotateBountyInvite(id: $id)
  }
`;

export const BOUNTY_STATUS_PILL: Record<BountyStatus, string> = {
  draft: 'bg-gray-100 text-gray-700',
  live: 'bg-green-100 text-green-800',
  archived: 'bg-amber-100 text-amber-800',
};

/** No delivery box ticked: it still pays, but nothing ever shows it. */
export function isSecret(bounty: Pick<AdminBounty, 'channelNotify' | 'channelPin' | 'channelEmail'>): boolean {
  return !bounty.channelNotify && !bounty.channelPin && !bounty.channelEmail;
}

export function cadenceLabel(cadence: AdminBounty['cadence']): string {
  return { once: 'Once', daily: 'Daily', weekly: 'Weekly', monthly: 'Monthly', each: 'Every time' }[cadence];
}

/** The period a cadence repeats on, as a word: "each week". */
export function periodWord(cadence: AdminBounty['cadence']): string {
  return { once: 'time', daily: 'day', weekly: 'week', monthly: 'month', each: 'time' }[cadence];
}

export function channelsLabel(
  bounty: Pick<AdminBounty, 'channelNotify' | 'channelPin' | 'channelEmail'> &
    Partial<Pick<AdminBounty, 'isInDailyEmail'>>
): string {
  if (isSecret(bounty)) return 'Secret';
  const on = [
    bounty.channelNotify && 'Inbox',
    bounty.channelPin && 'Pinned',
    bounty.isInDailyEmail && 'Daily email',
    bounty.channelEmail && 'Own email',
  ];
  return on.filter(Boolean).join(' · ');
}

function townSquareBase(): string {
  return typeof __TOWN_SQUARE_URL__ !== 'undefined' ? __TOWN_SQUARE_URL__ : '';
}

/** The bounty's canonical member page, with its panel open on arrival. */
export function bountyPageUrl(slug: string): string {
  return `${townSquareBase()}/bounties/${slug}?open=1`;
}

/** An invite-only bounty's shareable link. */
export function bountyInviteUrl(token: string): string {
  return `${townSquareBase()}/bounties/invite/${token}`;
}

/** The step harness narrowed to one step — its real panel, without completing anything. */
export function stepPanelPreviewUrl(stepCode: string): string {
  return `${townSquareBase()}/quests/steps?step=${encodeURIComponent(stepCode)}`;
}

/** How the admin names each kind, on the list and in the editor. */
export const KIND_LABEL: Record<BountyKind, string> = {
  task: 'Bounty',
  message: 'Message',
  suggestions: 'Artist cards',
};

/** The kind in a sentence — "Publish this message?". */
export function kindNoun(kind: BountyKind | undefined): string {
  if (kind === 'message') return 'message';
  if (kind === 'suggestions') return 'artist-card item';
  return 'bounty';
}
